// ⚠️ This file is over 150 lines. Consider splitting it.
import React, { useState, useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import Input from '../UI/Input'
import Button from '../UI/Button'

const Register = ({ onRegister }) => {
    const [searchParams] = useSearchParams()
    const inviteToken = searchParams.get('invite')
    const displayPartnerParam = searchParams.get('displayPartner')

    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        password: '',
        invite: inviteToken || '',
        turnstile: '',
        isDisplayPartner: displayPartnerParam === 'true',
        isDuesMember: false,
    })
    const [error, setError] = useState(null)
    const [fieldErrors, setFieldErrors] = useState({})
    const [pending, setPending] = useState(false)
    const [pendingMessage, setPendingMessage] = useState('')
    const [isSubmitting, setIsSubmitting] = useState(false)

    useEffect(() => {
        if (inviteToken) {
            setFormData((prev) => ({ ...prev, invite: inviteToken }))
        }
    }, [inviteToken])

    const captchaRef = useRef(null)
    useEffect(() => {
        let timerId
        const tryRender = () => {
            if (window.turnstile && captchaRef.current) {
                window.turnstile.render(captchaRef.current, {
                    sitekey: import.meta.env.VITE_TURNSTILE_SITE_KEY,
                    callback: window.onTurnstileSuccess,
                    action: 'register',
                    theme: 'light',
                })
            } else {
                timerId = setTimeout(tryRender, 500)
            }
        }
        tryRender()
        return () => clearTimeout(timerId)
    }, [])

    const handleChange = (e) => {
        const { name, type, value, checked } = e.target
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }))
    }

    const validate = () => {
        const errors = {}
        if (!formData.firstName.trim()) errors.firstName = 'First name is required.'
        if (!formData.lastName.trim()) errors.lastName = 'Last name is required.'
        if (!formData.email.trim()) errors.email = 'Email is required.'
        if (!formData.password) errors.password = 'Password is required.'
        else if (formData.password.length < 8) errors.password = 'Password must be at least 8 characters.'
        return errors
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        setError(null)
        const errors = validate()
        setFieldErrors(errors)
        if (Object.keys(errors).length > 0) return

        const captchaToken = document.querySelector('[name="cf-turnstile-response"]')?.value

        if (!captchaToken) {
            setError('CAPTCHA verification failed. Please try again.')
            return
        }

        const payload = {
            ...formData,
            turnstile: captchaToken,
        }

        setIsSubmitting(true)
        try {
            const response = await fetch(`${import.meta.env.VITE_BACKEND_URL}/auth/register`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            })

            if (response.status === 202) {
                let data = {}
                try {
                    data = await response.json()
                } catch (_) {}
                setPendingMessage(data.message || 'Your access is pending approval')
                setPending(true)
                return
            }

            const contentType = response.headers.get('content-type')
            if (!response.ok) {
                if (contentType && contentType.includes('application/json')) {
                    const data = await response.json()
                    setError(data.error || 'Registration failed.')
                } else {
                    setError('Unexpected server response.')
                }
                return
            }

            setPendingMessage('Registration successful! Please check your email to verify your account.')
            setPending(true)
        } catch (error) {
            setError('Server error. Try again later.')
        } finally {
            setIsSubmitting(false)
        }
    }

    if (pending) {
        return (
            <div className="max-w-md mx-auto bg-bg-light p-8 rounded-lg shadow-md border border-gray-200 mt-10">
                <h2 className="text-3xl font-bold text-center mb-6">{pendingMessage}</h2>
            </div>
        )
    }

    return (
        <div className="max-w-md mx-auto bg-bg-light p-8 rounded-lg shadow-md border border-gray-200 mt-10">
            <h2 className="text-3xl font-bold text-center mb-6">Create an Account</h2>
            {inviteToken && (
                <p className="text-warning text-sm font-medium text-center mb-2">
                    ⚠️ You are signing up as an <strong>Admin</strong>.
                </p>
            )}
            <form onSubmit={handleSubmit} className="flex flex-col space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        label="First Name"
                        error={fieldErrors.firstName}
                        placeholder="First Name"
                        required
                    />
                    <Input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        label="Last Name"
                        error={fieldErrors.lastName}
                        placeholder="Last Name"
                        required
                    />
                </div>
                <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    label="Email"
                    error={fieldErrors.email}
                    placeholder="Email"
                    required
                />
                <Input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    label="Phone Number"
                    placeholder="Phone Number"
                    required
                />
                <Input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    label="Password"
                    error={fieldErrors.password}
                    placeholder="Password"
                    required
                />
                <div className="mb-4 flex items-center">
                    <input
                        type="checkbox"
                        name="isDisplayPartner"
                        id="isDisplayPartner"
                        checked={formData.isDisplayPartner}
                        onChange={handleChange}
                        className="mr-2"
                    />
                    <label htmlFor="isDisplayPartner" className="text-sm">
                        I would like to sign up as a Display Partner to host community event displays
                    </label>
                </div>
                <div className="mb-4 flex items-center">
                    <input
                        type="checkbox"
                        name="isDuesMember"
                        id="isDuesMember"
                        checked={formData.isDuesMember}
                        onChange={handleChange}
                        className="mr-2"
                    />
                    <label htmlFor="isDuesMember" className="text-sm">
                        I am a current dues-paying member of the Longview Downtowners Association
                    </label>
                </div>

                <div className="mb-4">
                    <div ref={captchaRef} className="flex justify-center items-center py-4"></div>
                    <input type="hidden" name="cf-turnstile-response" />
                </div>

                {error && <p className="text-error text-sm">{error}</p>}
                <Button type="submit" size="lg" loading={isSubmitting} className="w-full mt-4">
                    Register
                </Button>
            </form>
        </div>
    )
}

export default Register
